import { motion } from 'framer-motion'
import {
  Fragment,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { hasAtLeast, isEmpty, isNullish } from 'remeda'
import { useMediaQuery } from 'usehooks-ts'
import { BreadcrumbsHeader } from '@components/BreadcrumbsHeader/BreadcrumbsHeader'
import { ConditionalShopTheLook } from '@components/ConditionalShopTheLook/ConditionalShopTheLook'
import { MatchingProductsSection } from '@components/MatchingProductsSection/MatchingProductsSection'
import { PDPComplementaryProducts } from '@components/PDPComplementaryProducts/PDPComplementaryProducts'
import { RecentProductsSlider } from '@components/RecentProductsSlider/RecentProductsSlider'
import { SimilarProductsSlider } from '@components/SimilarProductsSlider/SimilarProductsSlider'
import { SizeGuideModal } from '@components/SizeGuideModal/SizeGuideModal'
import { SizePicker } from '@components/SizePicker/SizePicker'
import { CustomThemeProvider } from '@helpers'
import { colorsPalette } from '@velasca/constants'
import { useHeader } from '@velasca/hooks/use-header'
import { useQueryParams } from '@velasca/hooks/use-query-params'
import { useRecentProducts } from '@velasca/hooks/use-recent-products'
import { DetailsCard } from '@velasca/ui/details-card'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { KlarnaPlacement } from '@velasca/ui/klarna-placement'
import { MediaCarousel } from '@velasca/ui/media-carousel'
import { ProductPage } from '@velasca/ui/product-page'
import { SansText } from '@velasca/ui/sans-text'
import { StampedReviews } from '@velasca/ui/stamped-reviews'
import { handleError } from '@velasca/utils/handle-error'
import { scrollTo } from '@velasca/utils/scroll-to'
import { ImagesPageProduct } from '../ImagesPageProduct/ImagesPageProduct'
import InfoBlockHeader from '../InfoBlockHeader/InfoBlockHeader'
import MoreDetails from '../MoreDetails'
import type { ApparelProductPageProps } from './ApparelProductPage.types'

const NO_MEDIA_ERROR_MESSAGE = 'There are currently no media for this product.'

const FooterLinks = ({
  footerLinks,
  onLinkClick,
}: {
  footerLinks:
    | {
        link: string
        description: string
      }[]
    | undefined
  onLinkClick?: (link: string) => void
}) => {
  const headerHeightPx = useHeader(({ headerHeight }) => headerHeight)

  if (!footerLinks || isEmpty(footerLinks)) {
    return
  }

  return (
    <ProductPage.Footer>
      <ProductPage.Hr />
      <ProductPage.InfoLinks length={footerLinks?.length}>
        {footerLinks?.map(({ link, description }, index) => {
          return (
            <Fragment key={index}>
              <SansText
                fontStyle="italic"
                color="primary"
                data-link-to={link}
                onClick={() => {
                  // Allow parent to switch tab (desktop) before scrolling
                  onLinkClick?.(link)
                  // Defer scroll to let the DOM update after tab switch
                  setTimeout(() => {
                    scrollTo(link, headerHeightPx)
                  }, 0)
                }}
              >
                {description}
              </SansText>
              {index !== footerLinks.length - 1 && <ProductPage.Separator />}
            </Fragment>
          )
        })}
      </ProductPage.InfoLinks>
      <ProductPage.Hr />
    </ProductPage.Footer>
  )
}

export const ApparelProductPage = ({
  product,
  moreDetailsInfo,
  materialSection,
  constructionSection,
  featuredDetailSection,
  contentKitSection,
  collectionBrandSection,
  careSection,
  productInstructions,
  madeInItalySection,
  manufacturingSection,
  packagingSection,
  sizeGuideSection,
  notifyMeTag,
  productMedia,
  breadcrumbs,
  complementaryProductSettings = {
    enabled: false,
    editorialImage: null,
  },
  relatedProductsSettings = {
    enabled: false,
  },
  recentProductsSettings = {
    enabled: false,
    swapImages: false,
  },
}: ApparelProductPageProps) => {
  const {
    productId,
    url,
    title,
    handle,
    price,
    rawPrice,
    compareAtPrice,
    rawCompareAtPrice,
    notifyMe,
    sizeAndFit,
    description,
    featuredImage,
    medias,
    loading = false,
    sizes,
    pairWithText,
    pairWithProducts,
    collectionBrandName,
    productType = '',
    seasonName,
    soleDesc,
    relatedSoles,
    shopTheLookIDs,
    matchingProducts,
    matchingProductsTitle,
  } = product

  useEffect(() => {
    if (!sizeGuideSection?.sizeGuide) {
      handleError(`Missing size guide metaobject handle for product ${handle}`)
    }
  }, [handle, sizeGuideSection?.sizeGuide])

  const filteredMedias = useMemo(
    () =>
      medias?.filter((media) => {
        return (
          media.type !== 'image' ||
          (media.type === 'image' &&
            !media.src?.includes('_08.') &&
            !media.src?.includes('_09.') &&
            !media.src?.includes('ZX_') &&
            !media.src?.includes('ZZ_'))
        )
      }),
    [medias],
  )

  useEffect(() => {
    const apparelProductSkeleton = document.querySelector(
      '#apparel-product-skeleton',
    )

    if (apparelProductSkeleton) {
      apparelProductSkeleton.classList.add('react-loaded')
    }
  }, [])

  const [isModalOpen, setIsModalOpen] = useQueryParams<boolean>('size-guide', {
    type: 'boolean',
  })

  useEffect(() => {
    if (!sizeGuideSection?.sizeGuide) {
      setIsModalOpen(false)

      return
    }

    if (!sizeGuideSection.sizeGuide.table) {
      handleError(
        `Missing size guide table metaobject for ${sizeGuideSection.sizeGuide.sizeGuideId}`,
      )
    }
  }, [setIsModalOpen, sizeGuideSection])

  // TODO: this is a really bad pattern. In fact I need to pass skip: true to AVOID making an unnecessary fetch request.
  // Better pattern: create a trackRecent function and call it when needed. Update: the hook already has a setRecentProduct
  // that does exactly what shown below.
  // const { trackRecent } = useRecentProducts()
  // useEffect(() => {
  //   trackRecent(productId)
  // }, [])
  // Another way of doing it would be separating tracking hook from fetching hook. They are two different and loosely related features.
  // const { trackRecent } = useRecentProductTracker()
  // same as above
  // const { recentProducts, loading, error } = useRecentProducts()
  useRecentProducts({ productId, skip: true })

  const promotionalBannerHeight = useHeader(
    (state) => state.promotionalBannerHeight,
  )
  const navbarHeight = useHeader((state) => state.navbarHeight)
  const isDesktop = useMediaQuery('(min-width: 992px)')

  const { t } = useTranslation('product')

  const groupedSections = [
    materialSection &&
      !isEmpty(materialSection) && {
        id: 'material-section',
        cta: t('sectionCTAMaterial'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard {...materialSection} id="material-section" />
          </ErrorBoundary>
        ),
      },
    constructionSection &&
      !isEmpty(constructionSection) && {
        id: 'construction-section',
        cta: t('sectionCTAconstruction'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard {...constructionSection} id="construction-section" />
          </ErrorBoundary>
        ),
      },
    featuredDetailSection &&
      !isEmpty(featuredDetailSection) && {
        id: 'featured-detail-section',
        cta: t('sectionCTAfeatureDetails'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard
              {...featuredDetailSection}
              id="featured-detail-section"
            />
          </ErrorBoundary>
        ),
      },
    careSection &&
      !isEmpty(careSection) && {
        id: 'care-section',
        cta: t('sectionCTAproductCare'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard {...careSection} isCollapsible id="care-section" />
          </ErrorBoundary>
        ),
      },
    contentKitSection &&
      !isEmpty(contentKitSection) && {
        id: 'kit-section',
        cta: t('sectionCTAkit'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard {...contentKitSection} id="kit-section" />
          </ErrorBoundary>
        ),
      },
    productInstructions &&
      !isEmpty(productInstructions) && {
        id: 'instructions-section',
        cta: t('sectionCTAproductInstructions'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard {...productInstructions} id="instructions-section" />
          </ErrorBoundary>
        ),
      },
    manufacturingSection &&
      !isEmpty(manufacturingSection) && {
        id: 'manufacturing-section',
        cta: t('sectionCTAmanufacturing'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard
              {...manufacturingSection}
              isCollapsible
              id="manufacturing-section"
            />
          </ErrorBoundary>
        ),
      },
    packagingSection &&
      !isEmpty(packagingSection) &&
      !!packagingSection.title &&
      !!packagingSection.description && {
        id: 'packaging-section',
        cta: t('sectionCTApackaging'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard
              {...packagingSection}
              isCollapsible
              id="packaging-section"
            />
          </ErrorBoundary>
        ),
      },
    madeInItalySection &&
      !isEmpty(madeInItalySection) && {
        id: 'made-in-italy-section',
        cta: t('sectionCTAmadeInItaly'),
        component: (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DetailsCard {...madeInItalySection} id="made-in-italy-section" />
          </ErrorBoundary>
        ),
      },
  ].filter(Boolean) as { id: string; cta: string; component: ReactElement }[]

  const [desktopSelectedSection, setDesktopSelectedSection] = useState(0)

  const groupedIdToIndex = useMemo(() => {
    const map: Record<string, number> = {}

    groupedSections.forEach((section, index) => {
      map[`#${section.id}`] = index
    })

    return map
  }, [groupedSections])

  const footerLinks = [
    ...(moreDetailsInfo
      ? [
          {
            link: '#more-details',
            description: t('moreDetailsTitle'),
          },
        ]
      : []),
    ...(materialSection
      ? [
          {
            link: '#material-section',
            description: t('materialTitle'),
          },
        ]
      : []),
    ...(constructionSection
      ? [
          {
            link: '#construction-section',
            description: t('constructionTitle'),
          },
        ]
      : []),
    ...(careSection
      ? [{ link: '#care-section', description: t('careTitle') }]
      : []),
    ...(manufacturingSection
      ? [
          {
            link: '#manufacturing-section',
            description: t('sectionCTAmanufacturing'),
          },
        ]
      : []),
    ...(contentKitSection
      ? [{ link: '#kit-section', description: t('contentTitle') }]
      : []),
    ...(productInstructions
      ? [{ link: '#instructions-section', description: t('instructionsTitle') }]
      : []),
  ]

  return (
    <CustomThemeProvider>
      {isDesktop && <BreadcrumbsHeader breadcrumbs={breadcrumbs} />}
      <ProductPage.MainContainer>
        <ProductPage.MainBlock>
          {isDesktop ? (
            <ProductPage.ImageBlock>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {filteredMedias && !isEmpty(filteredMedias) ? (
                  <ImagesPageProduct
                    zoom
                    thumbnailSidebar
                    medias={filteredMedias}
                    loading={loading}
                    color={description?.color}
                    material={description?.composition}
                    productTitle={title}
                    productType={productType}
                    shopTheLookIDs={shopTheLookIDs}
                    editorialImage={complementaryProductSettings.editorialImage}
                  />
                ) : (
                  <div>{NO_MEDIA_ERROR_MESSAGE}</div>
                )}
              </ErrorBoundary>
            </ProductPage.ImageBlock>
          ) : (
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              {!productMedia || isEmpty(productMedia.slides) ? (
                <div>{NO_MEDIA_ERROR_MESSAGE}</div>
              ) : (
                <ConditionalShopTheLook
                  shopTheLookIDs={shopTheLookIDs}
                  editorialImage={complementaryProductSettings.editorialImage}
                  condition={Boolean(
                    shopTheLookIDs && hasAtLeast(shopTheLookIDs, 1),
                  )}
                >
                  <MediaCarousel
                    hasZoom
                    forceBullets
                    hasArrows={false}
                    bulletsType="squared"
                    media={productMedia}
                  />
                </ConditionalShopTheLook>
              )}
            </ErrorBoundary>
          )}

          <ProductPage.ProductInfoBlock
            headerHeight={promotionalBannerHeight + navbarHeight}
            hasCollectionBrandName={Boolean(collectionBrandName)}
            style={
              isDesktop
                ? {
                    position: 'sticky',
                    top: promotionalBannerHeight + navbarHeight,
                  }
                : undefined
            }
          >
            {handle && !isNullish(price) ? (
              <InfoBlockHeader
                collectionBrandName={collectionBrandName}
                description={description}
                handle={handle}
                compareAtPrice={compareAtPrice}
                rawCompareAtPrice={rawCompareAtPrice}
                price={price}
                rawPrice={rawPrice}
                title={title}
                pairWithText={pairWithText}
                pairWithProducts={pairWithProducts}
                productType={productType}
                seasonName={seasonName}
                relatedSoles={relatedSoles}
                soleDesc={soleDesc}
                sizeAndFit={sizeAndFit}
              />
            ) : null}
            {sizes ? (
              <SizePicker
                product={product}
                price={price}
                setIsModalOpen={setIsModalOpen}
                title={t('sizeTitle')}
                link={t('sizeLink')}
                sizes={sizes}
                notifyMeTag={notifyMeTag}
                notifyMe={notifyMe}
                showButton={
                  sizeGuideSection?.sizeGuide?.sizeGuideId !== undefined
                }
              />
            ) : null}
            {rawPrice ? <KlarnaPlacement amount={rawPrice} /> : null}
            <FooterLinks
              footerLinks={footerLinks}
              onLinkClick={(link) => {
                if (!isDesktop) {
                  return
                }

                const idx = groupedIdToIndex[link]

                if (typeof idx === 'number') {
                  setDesktopSelectedSection(idx)
                }
              }}
            />
            {isDesktop && (
              <MatchingProductsSection
                products={matchingProducts ?? []}
                title={matchingProductsTitle ?? undefined}
              />
            )}
          </ProductPage.ProductInfoBlock>

          {isModalOpen && sizeGuideSection?.sizeGuide && (
            <SizeGuideModal
              {...sizeGuideSection}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
            />
          )}
        </ProductPage.MainBlock>
      </ProductPage.MainContainer>

      <ProductPage.Sliders>
        {complementaryProductSettings.enabled && handle && (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <PDPComplementaryProducts
              productHandle={handle}
              editorialImage={complementaryProductSettings.editorialImage}
              productMedias={medias}
            />
          </ErrorBoundary>
        )}
        {relatedProductsSettings.enabled && handle && (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <SimilarProductsSlider productHandle={handle} />
          </ErrorBoundary>
        )}
      </ProductPage.Sliders>

      <ProductPage.ProductSectionsContainer>
        <ProductPage.ProductSections
          className={isDesktop ? 'product-section-desktop' : ''}
        >
          {collectionBrandSection && !isEmpty(collectionBrandSection) && (
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <DetailsCard
                {...collectionBrandSection}
                hasMultipleImages
                videoUrl={collectionBrandSection.videoUrl}
                id="collection-brand-section"
              />
            </ErrorBoundary>
          )}
          {moreDetailsInfo && !isEmpty(moreDetailsInfo) && (
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <MoreDetails
                title={t('moreDetailsTitle')}
                details={moreDetailsInfo}
              />
            </ErrorBoundary>
          )}

          {isDesktop && !isEmpty(groupedSections) && (
            <div className="tablist-container">
              <nav aria-label="Section navigation">
                <ul role="tablist" aria-orientation="vertical">
                  {groupedSections.map((section, index) => (
                    <li key={index} role="presentation">
                      <button
                        id={`section-tab-${index}`}
                        role="tab"
                        aria-selected={index === desktopSelectedSection}
                        aria-controls={`section-panel-${index}`}
                        tabIndex={index === desktopSelectedSection ? 0 : -1}
                        style={{
                          color:
                            index === desktopSelectedSection
                              ? colorsPalette.dark
                              : colorsPalette.metal400,
                          borderLeft:
                            index === desktopSelectedSection
                              ? `3px solid ${colorsPalette.dark}`
                              : '3px solid transparent',
                        }}
                        onClick={() => {
                          setDesktopSelectedSection(index)
                        }}
                      >
                        {section.cta}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="tablist-content">
                {groupedSections.map((section, index) => {
                  return (
                    index === desktopSelectedSection && (
                      <motion.div
                        key={section.cta}
                        id={`section-panel-${index}`}
                        role="tabpanel"
                        aria-labelledby={`section-tab-${index}`}
                        tabIndex={0}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {section.component}
                      </motion.div>
                    )
                  )
                })}
              </div>
            </div>
          )}

          {!isDesktop && !isEmpty(groupedSections) && (
            <>
              {groupedSections.map((section) => (
                <Fragment key={section.cta}>{section.component}</Fragment>
              ))}
            </>
          )}
        </ProductPage.ProductSections>
      </ProductPage.ProductSectionsContainer>

      <ErrorBoundary fallback={null}>
        <StampedReviews
          description={description}
          featuredImage={featuredImage}
          handle={handle}
          productId={productId}
          title={title}
          url={url}
        />
      </ErrorBoundary>

      <ProductPage.Sliders>
        {recentProductsSettings.enabled && (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <RecentProductsSlider
              showProductType
              swapImages={recentProductsSettings.swapImages}
            />
          </ErrorBoundary>
        )}
      </ProductPage.Sliders>
    </CustomThemeProvider>
  )
}
